/**
 * ScopeOps Admin Performance Dashboard
 * PERF-S3-006: Real-time performance monitoring UI
 * 
 * Created: March 12, 2026
 * Sprint 3: Performance Epic
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Activity, Server, Database, Cpu, HardDrive, Clock,
  AlertTriangle, CheckCircle, RefreshCw, TrendingUp,
  TrendingDown, Minus, Zap, BarChart3, PieChart
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// =============================================================================
// METRIC CARD COMPONENT
// =============================================================================
const MetricCard = ({ title, value, unit, icon: Icon, trend, status, subtitle }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'healthy': return 'text-green-600 bg-green-100';
      case 'warning': return 'text-amber-600 bg-amber-100';
      case 'critical': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getTrendIcon = () => {
    if (trend > 0) return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (trend < 0) return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-gray-500">{title}</span>
        <div className={`p-2 rounded-lg ${getStatusColor()}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="flex items-end gap-2">
        <span className="text-2xl font-bold text-gray-900">{value}</span>
        {unit && <span className="text-sm text-gray-500 mb-1">{unit}</span>}
        {trend !== undefined && getTrendIcon()}
      </div>
      {subtitle && (
        <p className="text-xs text-gray-400 mt-1">{subtitle}</p>
      )}
    </div>
  );
};

// =============================================================================
// PROGRESS BAR COMPONENT
// =============================================================================
const ProgressBar = ({ value, max = 100, label, color = 'purple' }) => {
  const percent = Math.min((value / max) * 100, 100);
  const getColor = () => {
    if (percent > 90) return 'bg-red-500';
    if (percent > 70) return 'bg-amber-500';
    return `bg-${color}-500`;
  };

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-gray-600">{label}</span>
        <span className="font-medium text-gray-900">{percent.toFixed(1)}%</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div 
          className={`h-full ${getColor()} transition-all duration-500`}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
};

// =============================================================================
// LATENCY CHART COMPONENT
// =============================================================================
const LatencyChart = ({ data }) => {
  if (!data) return null;
  
  const { p50, p95, p99 } = data;
  const maxValue = Math.max(p50 || 0, p95 || 0, p99 || 0, 100);
  
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-500">p50</span>
        <div className="flex-1 mx-4">
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-green-500"
              style={{ width: `${(p50 / maxValue) * 100}%` }}
            />
          </div>
        </div>
        <span className="text-sm font-medium text-gray-900 w-16 text-right">{p50}ms</span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-500">p95</span>
        <div className="flex-1 mx-4">
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-amber-500"
              style={{ width: `${(p95 / maxValue) * 100}%` }}
            />
          </div>
        </div>
        <span className="text-sm font-medium text-gray-900 w-16 text-right">{p95}ms</span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-500">p99</span>
        <div className="flex-1 mx-4">
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-red-500"
              style={{ width: `${(p99 / maxValue) * 100}%` }}
            />
          </div>
        </div>
        <span className="text-sm font-medium text-gray-900 w-16 text-right">{p99}ms</span>
      </div>
    </div>
  );
};

// =============================================================================
// ENDPOINT TABLE COMPONENT
// =============================================================================
const EndpointTable = ({ endpoints }) => {
  if (!endpoints || endpoints.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No endpoint data available
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            <th className="pb-3">Endpoint</th>
            <th className="pb-3 text-right">Requests</th>
            <th className="pb-3 text-right">Avg (ms)</th>
            <th className="pb-3 text-right">p95 (ms)</th>
            <th className="pb-3 text-right">Error %</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {endpoints.slice(0, 10).map((ep, idx) => (
            <tr key={idx} className="text-sm">
              <td className="py-2">
                <span className={`inline-block w-12 text-xs font-medium rounded px-1 mr-2 ${
                  ep.method === 'GET' ? 'bg-green-100 text-green-700' :
                  ep.method === 'POST' ? 'bg-blue-100 text-blue-700' :
                  ep.method === 'PUT' ? 'bg-amber-100 text-amber-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {ep.method}
                </span>
                <span className="text-gray-700 font-mono text-xs">{ep.path}</span>
              </td>
              <td className="py-2 text-right text-gray-900">{ep.request_count}</td>
              <td className="py-2 text-right text-gray-600">{ep.avg_duration_ms}</td>
              <td className="py-2 text-right text-gray-600">{ep.p95}</td>
              <td className={`py-2 text-right ${ep.error_rate > 5 ? 'text-red-600' : 'text-gray-600'}`}>
                {ep.error_rate}%
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// =============================================================================
// MAIN DASHBOARD COMPONENT
// =============================================================================
const PerformanceDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [healthData, setHealthData] = useState(null);
  const [metricsData, setMetricsData] = useState(null);
  const [endpointData, setEndpointData] = useState(null);
  const [systemData, setSystemData] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const loadData = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    
    try {
      // Get auth token
      const token = localStorage.getItem('simpliview_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Fetch all data in parallel - handle individual failures gracefully
      const results = await Promise.allSettled([
        axios.get(`${BACKEND_URL}/api/health/detailed`, { headers }),
        axios.get(`${BACKEND_URL}/api/telemetry/metrics`, { headers }),
        axios.get(`${BACKEND_URL}/api/telemetry/endpoints`, { headers }),
        axios.get(`${BACKEND_URL}/api/telemetry/system`, { headers })
      ]);
      
      // Extract successful results or null for failures
      const health = results[0].status === 'fulfilled' ? results[0].value.data : null;
      const metrics = results[1].status === 'fulfilled' ? results[1].value.data : null;
      const endpoints = results[2].status === 'fulfilled' ? results[2].value.data : null;
      const system = results[3].status === 'fulfilled' ? results[3].value.data : null;
      
      setHealthData(health);
      setMetricsData(metrics);
      setEndpointData(endpoints);
      setSystemData(system);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to load performance data:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => loadData(), 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  const getStatusFromPercent = (percent) => {
    if (percent > 90) return 'critical';
    if (percent > 70) return 'warning';
    return 'healthy';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-purple-600 animate-spin mx-auto" />
          <p className="text-sm text-gray-500 mt-2">Loading performance data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="performance-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Performance Dashboard</h1>
          <p className="text-gray-500 text-sm">
            Real-time system health and performance metrics
          </p>
        </div>
        <div className="flex items-center gap-4">
          {lastUpdated && (
            <span className="text-sm text-gray-400">
              Updated {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={() => loadData(true)}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* System Status Banner */}
      <div className={`mb-6 p-4 rounded-xl border ${
        healthData?.status === 'healthy' 
          ? 'bg-green-50 border-green-200' 
          : 'bg-amber-50 border-amber-200'
      }`}>
        <div className="flex items-center gap-3">
          {healthData?.status === 'healthy' ? (
            <CheckCircle className="w-6 h-6 text-green-600" />
          ) : (
            <AlertTriangle className="w-6 h-6 text-amber-600" />
          )}
          <div>
            <h3 className="font-semibold text-gray-900">
              System Status: {healthData?.status === 'healthy' ? 'All Systems Operational' : 'Degraded Performance'}
            </h3>
            <p className="text-sm text-gray-600">
              Uptime: {healthData?.uptime?.formatted || 'N/A'} | 
              Version: {healthData?.version?.api || 'N/A'}
            </p>
          </div>
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <MetricCard
          title="Total Requests"
          value={metricsData?.requests?.total || 0}
          icon={Activity}
          status="healthy"
          subtitle={`${metricsData?.requests?.requests_per_minute || 0} req/min`}
        />
        <MetricCard
          title="Error Rate"
          value={metricsData?.requests?.error_rate_percent || 0}
          unit="%"
          icon={AlertTriangle}
          status={metricsData?.requests?.error_rate_percent > 5 ? 'warning' : 'healthy'}
          subtitle={`${metricsData?.requests?.error_count || 0} errors`}
        />
        <MetricCard
          title="Avg Latency"
          value={metricsData?.latency_ms?.p50 || 0}
          unit="ms"
          icon={Zap}
          status={metricsData?.latency_ms?.p50 > 500 ? 'warning' : 'healthy'}
          subtitle={`p95: ${metricsData?.latency_ms?.p95 || 0}ms`}
        />
        <MetricCard
          title="Cache Hit Rate"
          value={metricsData?.cache?.hit_rate_percent || 0}
          unit="%"
          icon={Database}
          status={metricsData?.cache?.hit_rate_percent < 50 ? 'warning' : 'healthy'}
          subtitle={`${metricsData?.cache?.hits || 0} hits`}
        />
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* System Resources */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Server className="w-5 h-5 text-purple-600" />
            <h2 className="font-semibold text-gray-900">System Resources</h2>
          </div>
          <div className="space-y-4">
            <ProgressBar
              label="CPU Usage"
              value={systemData?.cpu?.percent || healthData?.system?.cpu_percent || metricsData?.system?.cpu_percent || 0}
              color="purple"
            />
            <ProgressBar
              label="Memory Usage"
              value={systemData?.memory?.percent || healthData?.system?.memory?.percent || metricsData?.system?.memory_percent || 0}
              color="blue"
            />
            <ProgressBar
              label="Disk Usage"
              value={systemData?.disk?.percent || healthData?.system?.disk?.percent || 0}
              color="green"
            />
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Memory Total</span>
              <p className="font-medium">{systemData?.memory?.total_gb || healthData?.system?.memory?.total_gb || 0} GB</p>
            </div>
            <div>
              <span className="text-gray-500">Disk Free</span>
              <p className="font-medium">{systemData?.disk?.free_gb || (healthData?.system?.disk?.total_gb - healthData?.system?.disk?.used_gb)?.toFixed(2) || 0} GB</p>
            </div>
            <div>
              <span className="text-gray-500">CPU Cores</span>
              <p className="font-medium">{systemData?.cpu?.count || 'N/A'}</p>
            </div>
            <div>
              <span className="text-gray-500">Process Memory</span>
              <p className="font-medium">{systemData?.process?.memory_mb || 'N/A'} {systemData?.process?.memory_mb ? 'MB' : ''}</p>
            </div>
          </div>
        </div>

        {/* Response Time Distribution */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-purple-600" />
            <h2 className="font-semibold text-gray-900">Response Time Percentiles</h2>
          </div>
          <LatencyChart data={metricsData?.latency_ms} />
          
          <div className="mt-6 pt-4 border-t border-gray-100">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Database Performance</h3>
            <LatencyChart data={metricsData?.database?.query_latency_ms} />
          </div>
        </div>
      </div>

      {/* Health Checks */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                healthData?.database?.status === 'healthy' 
                  ? 'bg-green-100' 
                  : 'bg-red-100'
              }`}>
                <Database className={`w-5 h-5 ${
                  healthData?.database?.status === 'healthy'
                    ? 'text-green-600'
                    : 'text-red-600'
                }`} />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Database</h3>
                <p className="text-sm text-gray-500">
                  {healthData?.database?.latency_ms || 0}ms latency
                </p>
              </div>
            </div>
            <span className={`px-2 py-1 text-xs font-medium rounded ${
              healthData?.database?.status === 'healthy'
                ? 'bg-green-100 text-green-700'
                : 'bg-red-100 text-red-700'
            }`}>
              {healthData?.database?.status || 'unknown'}
            </span>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                healthData?.cache?.status === 'healthy'
                  ? 'bg-green-100'
                  : 'bg-amber-100'
              }`}>
                <Zap className={`w-5 h-5 ${
                  healthData?.cache?.status === 'healthy'
                    ? 'text-green-600'
                    : 'text-amber-600'
                }`} />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Cache</h3>
                <p className="text-sm text-gray-500">
                  {metricsData?.cache?.hit_rate_percent || 0}% hit rate
                </p>
              </div>
            </div>
            <span className={`px-2 py-1 text-xs font-medium rounded ${
              healthData?.cache?.status === 'healthy'
                ? 'bg-green-100 text-green-700'
                : 'bg-amber-100 text-amber-700'
            }`}>
              {healthData?.cache?.status || 'unknown'}
            </span>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <Activity className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">API</h3>
                <p className="text-sm text-gray-500">
                  {metricsData?.requests?.requests_per_minute || 0} req/min
                </p>
              </div>
            </div>
            <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-700">
              operational
            </span>
          </div>
        </div>
      </div>

      {/* Top Endpoints Table */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="w-5 h-5 text-purple-600" />
          <h2 className="font-semibold text-gray-900">Top Endpoints by Traffic</h2>
        </div>
        <EndpointTable endpoints={endpointData?.endpoints} />
      </div>
    </div>
  );
};

export default PerformanceDashboard;
